import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

type HowBuyingWorksProps = {
  className?: string;
};

export function HowBuyingWorks({ className }: HowBuyingWorksProps) {
  const { language } = useLanguage();
  const L = carDetailsLabels(language).howBuyingWorks;
  const [open, setOpen] = useState(false);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-sky-500/[0.06] via-background to-emerald-500/[0.05]",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03] sm:px-5 sm:py-4"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold tracking-tight text-foreground">
            {L.title}
          </span>
          {!open ? (
            <span className="mt-0.5 block text-[12.5px] text-muted-foreground line-clamp-1">
              {L.teaser}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300",
            open && "rotate-180",
          )}
        />
      </button>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-3 border-t border-border/60 px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
            <div className="space-y-2.5">
              {(Array.isArray(L.body) ? L.body : [L.body]).map((para, i) => (
                <p
                  key={i}
                  className="text-[13.5px] leading-relaxed text-foreground/90"
                >
                  {para}
                </p>
              ))}
            </div>
            <p className="rounded-xl border border-amber-500/20 bg-amber-500/[0.08] px-3 py-2.5 text-[13px] leading-relaxed text-foreground/85">
              {L.privateNote}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
